import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import {
  QrCode,
  User,
  CheckCircle,
  LogOut,
  Loader2,
  X,
  AlertTriangle,
  Clock,
  Package,
  Camera,
  Activity,
  Smartphone,
  Trophy,
  Shield,
  Globe,
  XCircle,
} from 'lucide-react';
import { languageNames, Language } from '../../../i18n';
import { SupervisorOverrideModal } from '../../Operator/modals';
import RawMaterialIssueModal from '../../Operator/modals/RawMaterialIssueModal';
import LabelPreviewModal from '../../../components/LabelPreviewModal';
import CameraScannerModal from '../../../components/CameraScannerModal';
import { authService } from '../../../services/api';
import operatorService, { ProductionSession } from '../../../services/operatorService';
import { clearMobileState } from '../../../utils/mobileSessionStorage';
import { normalizeBadgeCode, extractEmployeeNumber } from '../../../utils/barcodeUtils';
import { formatYield } from '../../../hooks/useYieldFactor';
import toast from 'react-hot-toast';
import type { EmployeeInfo, SessionInfo } from '../../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
  location_code?: string;
  printer_mac_id?: string;
}

interface PackerModalsProps {
  embedded: boolean;
  session: SessionInfo;
  employee: EmployeeInfo;
  station: StationInfo;
  step: 'weight_entry' | 'printing' | 'scan_confirm' | 'success';
  boxCount: number;
  expectedBoxes: number;
  isSessionCompleted: boolean;
  isLoading: boolean;
  isClockingOut: boolean;
  isTakingBreak: boolean;
  // Scan out
  showScanOutModal: boolean;
  setShowScanOutModal: (v: boolean) => void;
  scanOutStep: 'station' | 'badge';
  setScanOutStep: (s: 'station' | 'badge') => void;
  pendingScanOutAction: 'menu' | 'break' | 'clockout' | 'end_session';
  setPendingScanOutAction: (a: 'menu' | 'break' | 'clockout' | 'end_session') => void;
  // Badge menu
  showBadgeMenu: boolean;
  setShowBadgeMenu: (v: boolean) => void;
  // Sub-units complete
  showSubUnitsCompleteModal: boolean;
  setShowSubUnitsCompleteModal: (v: boolean) => void;
  // Target reached
  showTargetReachedModal: boolean;
  setShowTargetReachedModal: (v: boolean) => void;
  // Side menu
  showSideMenu: boolean;
  setShowSideMenu: (v: boolean) => void;
  // Supervisor badge prompt
  showSupervisorBadgePrompt: boolean;
  setShowSupervisorBadgePrompt: (v: boolean) => void;
  supervisorBadgeInput: string;
  setSupervisorBadgeInput: (v: string) => void;
  isVerifyingSupervisor: boolean;
  setIsVerifyingSupervisor: (v: boolean) => void;
  // Supervisor override
  showSupervisorOverride: boolean;
  setShowSupervisorOverride: (v: boolean) => void;
  overrideSupervisor: { id: string; name: string; employee_number: string } | null;
  setOverrideSupervisor: (v: { id: string; name: string; employee_number: string } | null) => void;
  // Actions dropdown
  showActionsDropdown: boolean;
  setShowActionsDropdown: (v: boolean) => void;
  // Material issue
  showMaterialIssueModal: boolean;
  setShowMaterialIssueModal: (v: boolean) => void;
  // Label preview
  showLabelPreview: boolean;
  setShowLabelPreview: (v: boolean) => void;
  labelPreviewHtml: string;
  numberOfCopies: number;
  // Camera
  showCamera: boolean;
  setShowCamera: (v: boolean) => void;
  // Handlers
  handleClockOut: () => void;
  handleTakeBreak: () => void;
  handleCancelConfirm: () => void;
  handleConfirmScan: (barcode: string) => void;
  handleWeightEntryScan: (barcode: string) => void;
  handleSupervisorBadgeScan: (badgeCode: string) => void;
  handleManualWeightToggle: (mode: 'fixed' | 'catch' | 'scale') => void;
  // Session setter for RawMaterialIssueModal onSuccess
  setSession: React.Dispatch<React.SetStateAction<SessionInfo | null>>;
  // Yield
  apiYield: number | null;
}

const PackerModals: React.FC<PackerModalsProps> = ({
  embedded,
  session,
  employee,
  station,
  step,
  boxCount,
  expectedBoxes,
  isSessionCompleted,
  isLoading,
  isClockingOut,
  isTakingBreak,
  showScanOutModal,
  setShowScanOutModal,
  scanOutStep,
  setScanOutStep,
  pendingScanOutAction,
  setPendingScanOutAction,
  showBadgeMenu,
  setShowBadgeMenu,
  showSubUnitsCompleteModal,
  setShowSubUnitsCompleteModal,
  showTargetReachedModal,
  setShowTargetReachedModal,
  showSideMenu,
  setShowSideMenu,
  showSupervisorBadgePrompt,
  setShowSupervisorBadgePrompt,
  supervisorBadgeInput,
  setSupervisorBadgeInput,
  isVerifyingSupervisor,
  setIsVerifyingSupervisor,
  showSupervisorOverride,
  setShowSupervisorOverride,
  overrideSupervisor,
  setOverrideSupervisor,
  showActionsDropdown,
  setShowActionsDropdown,
  showMaterialIssueModal,
  setShowMaterialIssueModal,
  showLabelPreview,
  setShowLabelPreview,
  labelPreviewHtml,
  numberOfCopies,
  showCamera,
  setShowCamera,
  handleClockOut,
  handleTakeBreak,
  handleCancelConfirm,
  handleConfirmScan,
  handleWeightEntryScan,
  handleSupervisorBadgeScan,
  handleManualWeightToggle,
  setSession,
  apiYield,
}) => {
  const { t, language, setLanguage } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Navigate to waste tracking with fresh actual_output_weight from DB
  // (avoids using boxCount × unit_weight which loses decimal precision from scale).
  // WCO-2403: fetch by this screen's sub_session_number, not getMySession by employee.
  const navigateToWasteTracking = async (isEarly: boolean) => {
    let freshOutputWeight = session?.actual_output_weight;
    try {
      if (session?.sub_session_number) {
        const result = await operatorService.getSubSessionDetails(session.sub_session_number);
        if (result.session?.actual_output_weight != null) {
          freshOutputWeight = result.session.actual_output_weight;
        }
      }
    } catch {
      // Fall back to current session data if fetch fails
    }
    navigate('/mobile/waste-tracking', {
      state: {
        session: {
          ...session,
          total_boxes: boxCount,
          actual_output_weight: freshOutputWeight,
        },
        station,
        employee,
        boxCount,
        ...(isEarly ? { isEarlyCompletion: true } : {}),
      },
    });
  };

  return (
    <>
      {/* Scan Out Modal - Station then Badge verification */}
      {showScanOutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md mx-4 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-center text-gray-900 dark:text-white mb-2">
              {t('mobile.scanOut') || 'Scan Out'}
            </h3>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
              {scanOutStep === 'station'
                ? (t('mobile.scanStationBarcode') || 'Scan Station Barcode')
                : (t('mobile.scanYourBadge') || 'Scan Your Badge')
              }
            </p>

            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mb-6">
              <div className={`w-3 h-3 rounded-full ${scanOutStep === 'station' ? 'bg-red-500' : 'bg-green-500'}`} />
              <div className={`w-3 h-3 rounded-full ${scanOutStep === 'badge' ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            </div>

            {/* Scan input */}
            <div className="mb-6">
              <input
                type="text"
                key={scanOutStep} // Force re-render when step changes
                defaultValue={scanOutStep === 'station'
                  ? (station?.station_barcode || `#STN-${station?.station_id}`)
                  : employee.employee_number
                }
                autoFocus
                className="w-full px-4 py-3 text-lg border-2 border-amber-400 dark:border-amber-500 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = e.currentTarget.value.trim();
                    if (!value) return;

                    if (scanOutStep === 'station') {
                      // Verify station barcode matches current station
                      const expectedBarcode = station?.station_barcode || `#STN-${station?.station_id}`;
                      if (value === expectedBarcode || value === `#STN-${station?.station_id}`) {
                        setScanOutStep('badge');
                      } else {
                        toast.error(t('mobile.wrongStation') || 'Wrong station - please scan the correct station');
                      }
                    } else {
                      // Verify badge matches current employee
                      const badgeCode = normalizeBadgeCode(value);
                      const expectedBadge = `@+${employee.employee_number}`;
                      if (badgeCode === expectedBadge) {
                        setShowScanOutModal(false);
                        setScanOutStep('station');
                        // Dispatch based on pending action
                        if (pendingScanOutAction === 'break') {
                          setPendingScanOutAction('menu');
                          handleTakeBreak();
                        } else if (pendingScanOutAction === 'clockout') {
                          setPendingScanOutAction('menu');
                          handleClockOut();
                        } else if (pendingScanOutAction === 'end_session') {
                          setPendingScanOutAction('menu');
                          navigateToWasteTracking(true);
                        } else {
                          setShowBadgeMenu(true);
                        }
                      } else {
                        toast.error(t('mobile.wrongBadge') || 'Wrong badge - please scan your badge');
                      }
                    }
                  }
                }}
              />
            </div>

            <button
              onClick={() => {
                setShowScanOutModal(false);
                setScanOutStep('station');
                setPendingScanOutAction('menu');
              }}
              className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Badge Options Menu Modal */}
      {showBadgeMenu && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-t-2xl p-6 space-y-3">
            <h3 className="text-lg font-bold text-center text-gray-900 dark:text-white mb-4">
              {t('mobile.whatWouldYouLikeToDo') || 'What would you like to do?'}
            </h3>

            <button
              onClick={() => {
                setShowBadgeMenu(false);
                handleClockOut();
              }}
              disabled={isClockingOut}
              className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3"
            >
              <LogOut className="w-6 h-6" />
              {t('mobile.clockOut') || 'Clock Out'}
            </button>

            <button
              onClick={handleTakeBreak}
              disabled={isTakingBreak}
              className="w-full py-4 bg-amber-500 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {isTakingBreak ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Clock className="w-6 h-6" />
              )}
              {isTakingBreak ? (t('mobile.pausing') || 'Pausing...') : (t('mobile.takeBreak') || 'Take Break')}
            </button>

            <button
              onClick={() => {
                setShowBadgeMenu(false);
                navigateToWasteTracking(true);
              }}
              className="w-full py-4 bg-purple-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3"
            >
              <XCircle className="w-6 h-6" />
              {t('mobile.endSession') || 'End Session'}
            </button>

            <button
              onClick={() => setShowBadgeMenu(false)}
              className="w-full py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Sub-units Complete Modal */}
      {showSubUnitsCompleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mx-4 max-w-sm w-full text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {Math.floor(session?.item_sub_quantity || 0)}/{Math.floor(session?.item_sub_quantity || 0)} {t('mobile.subUnitsPacked') || 'Sub-units packed'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('mobile.nowPackTheBox') || 'Now pack the box'}
            </p>
            <button
              onClick={() => setShowSubUnitsCompleteModal(false)}
              className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg"
            >
              {t('common.ok') || 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Target Reached Modal - fullscreen, touch-friendly for 5" Munbyn screen */}
      {showTargetReachedModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full h-full md:max-w-xl md:mx-auto md:h-auto md:rounded-3xl flex flex-col items-center justify-center p-6">
            {/* Trophy icon */}
            <div className="w-24 h-24 md:w-28 md:h-28 mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <Trophy className="w-14 h-14 text-green-600 dark:text-green-400" />
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 text-center">
              {t('mobile.targetReached') || 'Target Reached!'}
            </h2>

            {/* Box count */}
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2 text-center">
              {t('mobile.targetReachedMessage') || "You've packed all the target boxes for this session."}
            </p>
            <p className="text-4xl md:text-5xl font-bold text-green-600 dark:text-green-400 mb-10">
              {boxCount} / {expectedBoxes}
            </p>

            {/* Two large stacked buttons */}
            <div className="w-full space-y-4">
              <button
                onClick={() => setShowTargetReachedModal(false)}
                className="w-full py-5 md:py-6 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl font-bold text-xl md:text-2xl"
              >
                {t('mobile.continuePacking') || 'Continue Packing'}
              </button>

              <button
                onClick={() => {
                  setShowTargetReachedModal(false);
                  navigateToWasteTracking(false);
                }}
                className="w-full py-5 md:py-6 bg-green-600 text-white rounded-2xl font-bold text-xl md:text-2xl"
              >
                {t('mobile.completeSession') || 'Complete Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Completed Overlay - blocks packing when sub-session is completed */}
      {isSessionCompleted && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full h-full flex flex-col items-center justify-center p-6">
            {/* Checkmark icon */}
            <div className="w-24 h-24 mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <CheckCircle className="w-14 h-14 text-gray-500 dark:text-gray-400" />
            </div>

            {/* Heading */}
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3 text-center">
              {t('mobile.sessionCompletedBySupervisor') || 'Session Completed'}
            </h2>

            {/* Message */}
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-2 text-center max-w-sm">
              {t('mobile.sessionCompletedMessage') || 'This session has been marked as completed. You can no longer pack boxes for this session.'}
            </p>

            {/* Box count summary */}
            <p className="text-2xl font-bold text-gray-500 dark:text-gray-400 mb-10">
              {boxCount} {t('mobile.boxes') || 'boxes'} {t('mobile.packed') || 'packed'}
            </p>

            {/* Return button */}
            <button
              onClick={() => {
                clearMobileState();
                navigate('/mobile/clock-in');
              }}
              className="w-full max-w-xs py-5 bg-blue-600 text-white rounded-2xl font-bold text-xl"
            >
              {t('mobile.returnToClockIn') || 'Return to Clock In'}
            </button>
          </div>
        </div>
      )}

      {/* Slide-out Side Menu — hidden when embedded in Layout (Layout has its own sidebar) */}
      {!embedded && showSideMenu && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSideMenu(false)}
          />
          {/* Menu Panel */}
          <div className="relative w-72 md:w-80 lg:w-96 max-w-[80%] bg-white dark:bg-gray-800 h-full shadow-xl flex flex-col">
            {/* Menu Header with Logo */}
            <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-700">
              <img
                src="/wf-removebg-preview.png"
                alt="Way Fong"
                className="h-12 w-auto"
              />
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {/* Desktop-only navigation items - hidden on mobile */}
              <Link
                to="/operator/production"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Package className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.productionEntry') || 'Production Entry'}
              </Link>

              <Link
                to="/operator/performance"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Activity className="w-5 h-5 mr-3 text-gray-400" />
                {t('dashboard.performance') || 'Performance'}
              </Link>

              <Link
                to="/operator/clock-in"
                onClick={() => setShowSideMenu(false)}
                className="hidden md:flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Smartphone className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.mobileView') || 'Mobile View'}
              </Link>

              {/* Divider - only show on desktop when nav links are visible */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700 hidden md:block" />

              {/* Session Actions */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  setPendingScanOutAction('break');
                  setScanOutStep('station');
                  setShowScanOutModal(true);
                }}
                disabled={isTakingBreak}
                className="w-full flex items-center px-3 py-3 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isTakingBreak ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <Clock className="w-5 h-5 mr-3" />
                )}
                {isTakingBreak ? (t('mobile.pausing') || 'Pausing...') : (t('mobile.takeBreak') || 'Take Break')}
              </button>

              <button
                onClick={() => {
                  setShowSideMenu(false);
                  setPendingScanOutAction('clockout');
                  setScanOutStep('station');
                  setShowScanOutModal(true);
                }}
                disabled={isClockingOut}
                className="w-full flex items-center px-3 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {isClockingOut ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5 mr-3" />
                )}
                {t('mobile.clockOut') || 'Clock Out'}
              </button>

              {/* End Session */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  setPendingScanOutAction('end_session');
                  setScanOutStep('station');
                  setShowScanOutModal(true);
                }}
                className="w-full flex items-center px-3 py-3 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 mr-3" />
                {t('mobile.endSession') || 'End Session'}
              </button>

              {/* Divider before special actions */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700" />

              {/* Raw Material Issue */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  setShowMaterialIssueModal(true);
                }}
                className="w-full flex items-center px-3 py-3 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
              >
                <AlertTriangle className="w-5 h-5 mr-3" />
                {t('materialIssue.menuButton')}
              </button>

              {/* Supervisor Override */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  setShowSupervisorBadgePrompt(true);
                }}
                className="w-full flex items-center px-3 py-3 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
              >
                <Shield className="w-5 h-5 mr-3" />
                {t('mobile.supervisorOverride') || 'Supervisor Override'}
              </button>

              {/* Divider before Language */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700" />

              {/* Language Switcher */}
              <button
                onClick={() => {
                  const langCycle: Language[] = ['en', 'zh-TW', 'es'];
                  const currentIndex = langCycle.indexOf(language);
                  const nextLang = langCycle[(currentIndex + 1) % langCycle.length];
                  setLanguage(nextLang);
                }}
                className="w-full flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Globe className="w-5 h-5 mr-3 text-gray-400" />
                <span>{t('nav.language') || 'Language'}</span>
                <span className="ml-auto text-sm font-medium text-blue-600 dark:text-blue-400">
                  {languageNames[language]}
                </span>
              </button>
            </nav>

            {/* User Info & Logout at Bottom */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold">
                  {employee.display_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{employee.display_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">#{employee.employee_number}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  logout();
                }}
                className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center gap-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout') || 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Badge Scan Prompt Modal */}
      {showSupervisorBadgePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                {t('mobile.supervisorOverride') || 'Supervisor Override'}
              </h3>
              <button
                onClick={() => {
                  setShowSupervisorBadgePrompt(false);
                  setSupervisorBadgeInput('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('mobile.scanSupervisorBadgeToContinue') || 'Scan supervisor badge to continue'}
            </p>

            <div className="space-y-3">
              <input
                type="text"
                value={supervisorBadgeInput}
                onChange={(e) => setSupervisorBadgeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && supervisorBadgeInput.trim()) {
                    handleSupervisorBadgeScan(supervisorBadgeInput.trim());
                  }
                }}
                placeholder="@+1234"
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-lg"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSupervisorBadgePrompt(false);
                    setSupervisorBadgeInput('');
                  }}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={() => {
                    if (supervisorBadgeInput.trim()) {
                      handleSupervisorBadgeScan(supervisorBadgeInput.trim());
                    }
                  }}
                  disabled={!supervisorBadgeInput.trim() || isVerifyingSupervisor}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isVerifyingSupervisor ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('common.verify') || 'Verify'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions Modal (desktop) */}
      {showActionsDropdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowActionsDropdown(false)} />
          <div className="relative z-10 w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('common.actions') || 'Actions'}</h3>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setShowActionsDropdown(false); setShowMaterialIssueModal(true); }}
                className="flex items-center gap-4 px-5 py-4 text-base font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-xl transition-colors"
              >
                <AlertTriangle className="w-6 h-6" />
                {t('materialIssue.menuButton')}
              </button>
              <button
                onClick={() => { setShowActionsDropdown(false); setShowSupervisorBadgePrompt(true); }}
                className="flex items-center gap-4 px-5 py-4 text-base font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-xl transition-colors"
              >
                <Shield className="w-6 h-6" />
                {t('mobile.supervisorOverride') || 'Supervisor Override'}
              </button>
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <button
                onClick={() => { setShowActionsDropdown(false); setPendingScanOutAction('break'); setScanOutStep('station'); setShowScanOutModal(true); }}
                disabled={isTakingBreak}
                className="flex items-center gap-4 px-5 py-4 text-base font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-xl transition-colors disabled:opacity-50"
              >
                {isTakingBreak ? <Loader2 className="w-6 h-6 animate-spin" /> : <Clock className="w-6 h-6" />}
                {isTakingBreak ? (t('mobile.pausing') || 'Pausing...') : (t('mobile.takeBreak') || 'Take Break')}
              </button>
              <button
                onClick={() => { setShowActionsDropdown(false); setPendingScanOutAction('clockout'); setScanOutStep('station'); setShowScanOutModal(true); }}
                disabled={isClockingOut}
                className="flex items-center gap-4 px-5 py-4 text-base font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-colors disabled:opacity-50"
              >
                {isClockingOut ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogOut className="w-6 h-6" />}
                {t('mobile.clockOut') || 'Scan Out'}
              </button>
              <button
                onClick={() => { setShowActionsDropdown(false); setPendingScanOutAction('end_session'); setScanOutStep('station'); setShowScanOutModal(true); }}
                className="flex items-center gap-4 px-5 py-4 text-base font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-xl transition-colors"
              >
                <XCircle className="w-6 h-6" />
                {t('mobile.endSession') || 'End Session'}
              </button>
            </div>
            <button
              onClick={() => setShowActionsDropdown(false)}
              className="w-full mt-4 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Raw Material Issue Modal */}
      {showMaterialIssueModal && session && (
        <RawMaterialIssueModal
          isOpen={showMaterialIssueModal}
          session={{
            sub_session_number: session.sub_session_number,
            session_number: session.session_number || session.sub_session_number,
            product_name: session.product_name,
            product_sku: session.product_sku,
            product_chinese: session.product_chinese,
            raw_material_item_number: '',
            raw_material_name: session.raw_material_name || '',
            raw_material_chinese: '',
            raw_material_lot: session.lot_number,
            station_number: station?.station_id || 0,
            station_name: station?.station_name || '',
            supervisor_name: '',
            session_status: session.status,
            status: session.status,
            initial_material_weight: session.initial_material_weight || 0,
            target_output_weight: session.target_output_weight,
            actual_output_weight: session.actual_output_weight,
            box_target_weight: session.product_unit_weight || session.unit_weight || 0,
            packing: 0,
            yield_factor: 0,
            waste_weight: session.waste_weight || 0,
            yield_percentage: session.yield_percentage || 0,
            expected_yield: session.expected_yield || 0,
            notes: null,
            started_at: session.started_at || null,
            completed_at: null,
          } as ProductionSession}
          operatorEmployeeNumber={employee?.employee_number || ''}
          operatorName={employee?.display_name || ''}
          onClose={() => setShowMaterialIssueModal(false)}
          onSuccess={() => {
            // Refresh session data (WCO-2403: keyed by this screen's sub-session)
            if (session?.sub_session_number && employee?.employee_number) {
              operatorService.getSubSessionDetails(session.sub_session_number).then(data => {
                const s = data.session;
                if (s) {
                  const newExpected = parseInt(String(s.expected_boxes)) || 0;
                  setSession(prev => prev ? {
                    ...prev,
                    initial_material_weight: s.initial_material_weight,
                    target_output_weight: s.target_output_weight,
                    expected_boxes: newExpected,
                  } : prev);
                  // If remaining material can't produce even 1 box, show target reached modal
                  if (newExpected === 0 || boxCount >= newExpected) {
                    setShowMaterialIssueModal(false);
                    setTimeout(() => setShowTargetReachedModal(true), 300);
                  }
                }
              }).catch((err) => {
                console.warn('Failed to refresh session data after material issue', err);
                toast.error(t('messages.refreshFailed'));
              });
            }
          }}
        />
      )}

      {/* Supervisor Override Modal */}
      {showSupervisorOverride && overrideSupervisor && session && (
        <SupervisorOverrideModal
          isOpen={showSupervisorOverride}
          onClose={() => {
            setShowSupervisorOverride(false);
            setOverrideSupervisor(null);
          }}
          supervisorId={overrideSupervisor.id}
          supervisorName={overrideSupervisor.name}
          supervisorEmployeeNumber={overrideSupervisor.employee_number}
          session={{
            session_number: session.session_number || session.sub_session_number || '',
            sub_session_number: session.sub_session_number,
            product_name: session.product_name,
            product_chinese: session.product_chinese,
            product_sku: session.product_sku,
            product_ingredient: session.product_ingredient,
            raw_material_lot: session.lot_number,
            station_id: station?.station_id || 0,
            station_name: station?.station_name || '',
            status: session.status,
            actual_output_weight: session.actual_output_weight,
            target_output_weight: session.target_output_weight,
            initial_material_weight: session.initial_material_weight || 0,
            weight_mode: session.weight_mode,
            // Yield tracking fields for End of Day Summary
            started_at: session.started_at || null,
            paused_duration_seconds: session.paused_duration_seconds,
            waste_weight: session.waste_weight || 0,
            yield_percentage: session.yield_percentage || 0,
            expected_yield: session.expected_yield || 0,
            expected_boxes: session.expected_boxes,
            total_boxes: boxCount, // Pass current packed box count
          } as ProductionSession}
          operatorEmployeeNumber={employee?.employee_number}
          operatorName={employee?.display_name}
          onManualWeightToggle={handleManualWeightToggle}
        />
      )}

      {/* Label Preview Modal (TEST MODE) */}
      <LabelPreviewModal
        isOpen={showLabelPreview}
        onClose={() => setShowLabelPreview(false)}
        labelHtml={labelPreviewHtml}
        copies={numberOfCopies}
      />

      {/* Camera Scanner Modal — backup for USB barcode scanner */}
      <CameraScannerModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onScan={(barcode) => {
          setShowCamera(false);
          if (step === 'scan_confirm') {
            handleConfirmScan(barcode);
          } else if (step === 'weight_entry') {
            handleWeightEntryScan(barcode);
          }
        }}
      />
    </>
  );
};

export default PackerModals;
